import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  AlertTriangle, Stethoscope, Search, Scissors, Pill, Flame, Heart,
  Clock, Calendar, MessageCircle, Phone, ArrowLeft, ArrowRight, ChevronRight
} from "lucide-react";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";

const symptoms = [
  "Abnormal vaginal bleeding",
  "Unusual vaginal discharge",
  "Persistent pelvic or abdominal pain",
  "Bloating or swelling",
  "Changes in menstrual cycle",
  "Unexplained weight loss",
];

const whenToConsult = [
  "You are above 25 years of age",
  "You are sexually active",
  "You have irregular or abnormal bleeding",
  "You have a family history of cancer",
  "You have HPV infection",
  "You have risk factors like obesity, smoking, or hormonal imbalance",
];

const diagnostics = [
  { label: "", detail: "Pap smear test (routine screening)" },
  { label: "", detail: "HPV testing" },
  { label: "", detail: "Colposcopy (detailed examination of cervix)" },
  { label: "", detail: "Cervical biopsy" },
  { label: "", detail: " Genetic testing for staging" },
];

const treatments = [
  {
    icon: Scissors,
    title: "Cervical Cancer Screening",
    description:
      "",
    points: [
      "Pap smear test",
      "HPV testing",
      "Colposcopy",
      "Helps detect precancerous changes early",
    ],
  },
  {
    icon: Pill,
    title: "Ovarian Cancer Evaluation",
    description:
      "",
    points: [
      "Ultrasound imaging",
      "Tumor markers (e.g., CA-125)",
      "Recommended in high-risk individuals",
    ],
  },
  {
    icon: Flame,
    title: "Uterine Cancer Assessment",
    description:
      "",
    points: [
      "Ultrasound for endometrial thickness",
      "Endometrial biopsy (if needed)",
    ],
  },
  {
    icon: Heart,
    title: "General Gynecologic Examination",
    description:
      "",
    points: [
      "Pelvic examination",
      "Risk assessment",
      "Preventive counseling",
    ],
  },
];

const recoveryTimeline = [
  {
    phase: "After Surgery",
    items: [
      "Hospital stay: 3–7 days)",
      "Initial recovery: 2–3 weeks",
      "Full recovery: 4–6 weeks",
    ],
  },
  {
    phase: "During Radiation/Chemotherapy",
    items: [
      "Treatment duration: 6–8 weeks (approx)",
      "Side effects may include fatigue, skin irritation, and mild discomfort",
    ],
  },
  {
    phase: "Follow-Up Care",
    items: [
      "Regular monitoring is essential",
      "Pap smears and imaging may be advised periodically",
    ],
  },
];

const faqs = [
  {
    q: "How often should I get screened for cervical cancer?",
    a: "Pap smear is usually recommended every 3 years, but frequency may vary based on age and risk.",
  },
  {
    q: "Is cancer screening painful?",
    a: "Most screening tests, like Pap smear and ultrasound, are quick and involve minimal discomfort.",
  },
  {
    q: "Can cancer be prevented through screening?",
    a: "Screening helps detect precancerous conditions early, which can be treated to prevent cancer.",
  },
  {
    q: "Do I need screening if I have no symptoms?",
    a: "Yes, screening is important even without symptoms to detect early changes.",
  },
  {
    q: "Is HPV vaccination necessary?",
    a: "HPV vaccination significantly reduces the risk of cervical cancer and is highly recommended.",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55 } },
};

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.07 } },
};

export default function OvarianCancerPage() {
  return (
    <div className="bg-[#F9F9F6] min-h-screen">
      {/* ─── STICKY HEADER ─── */}


      {/* ─── HERO / INTRO ─── */}
      <section className="pt-28 md:pt-36 pb-16 md:pb-24" data-testid="service-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          {/* Breadcrumb */}
          <motion.nav
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4 }}
            className="flex items-center gap-2 text-sm text-[#4A6359] mb-8"
            data-testid="service-breadcrumb"
          >
            <Link to="/" className="hover:text-[#1E3F33] transition-colors">Home</Link>
            <ChevronRight className="w-3.5 h-3.5" />
            <Link to="/#services" className="hover:text-[#1E3F33] transition-colors">Services</Link>
            <ChevronRight className="w-3.5 h-3.5" />
            <span className="text-[#1E3F33] font-medium">   Cancer Screening & Preventive Oncology  Treatment</span>
          </motion.nav>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="lg:col-span-7"
            >
              <p className="text-sm font-semibold tracking-[0.2em] uppercase text-[#8FA998] mb-4">
                Gynecologic Oncology
              </p>
              <h1
                data-testid="service-page-title"
                style={{ lineHeight: "1.2" }}
                className="font-serif text-4xl sm:text-3xl lg:text-6xl xl:text-5xl tracking-tighter leading-[0.95] text-[#1E3F33] mb-6"
              >
                Cancer Screening & Preventive Oncology in Kolkata – Early Detection by Dr. Neha Agarwalal
              </h1>
              <p className="text-base sm:text-lg text-[#4A6359] leading-relaxed max-w-2xl mb-8" data-testid="service-intro">
                Early detection is one of the most effective ways to prevent and successfully treat cancer. Many gynecologic cancers, including cervical and uterine cancer, can be detected at an early stage through regular screening. </p>
              <p className="text-base sm:text-lg text-[#4A6359] leading-relaxed max-w-2xl mb-8" data-testid="service-intro">
                Dr. Neha Agarwal, a leading gynecologic oncologist in Kolkata, offers comprehensive cancer screening and preventive oncology services to help women identify risks early and take proactive steps toward better health. </p>
              <p className="text-base sm:text-lg text-[#4A6359] leading-relaxed max-w-2xl mb-8" data-testid="service-intro">
                Her approach focuses on awareness, timely diagnosis, and prevention strategies tailored to each individual.</p>
              <div className="flex flex-wrap gap-3">
                <a
                  href="/book-appointment"
                  data-testid="service-hero-book-btn"
                  className="inline-flex items-center gap-2 bg-[#1E3F33] text-[#F9F9F6] px-7 py-3.5 rounded-full text-sm font-semibold hover:bg-[#2C5545] transition-colors"
                >
                  <Calendar className="w-4 h-4" />
                  Book Appointment
                </a>
                <a
                  href="https://wa.me/916291000829"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="service-hero-whatsapp-btn"
                  className="inline-flex items-center gap-2 border-2 border-[#1E3F33] text-[#1E3F33] px-7 py-3.5 rounded-full text-sm font-semibold hover:bg-[#1E3F33] hover:text-[#F9F9F6] transition-all"
                >
                  <MessageCircle className="w-4 h-4" />
                  WhatsApp
                </a>
              </div>
            </motion.div>

            {/* Quick Info Card */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="lg:col-span-5"
            >
              <div className="bg-[#F2EFE9] rounded-2xl md:rounded-[2rem] p-7 md:p-8" data-testid="service-quick-info">
                <h3 className="font-serif text-2xl text-[#1E3F33] mb-5">
                  Quick Overview
                </h3>

                <div className="space-y-4">

                  {/* Specialist */}
                  <div className="flex items-start gap-3">
                    <Stethoscope className="w-5 h-5 text-[#8FA998] mt-0.5 flex-shrink-0" />

                    <div>
                      <p className="text-sm font-semibold text-[#1E3F33]">
                        Specialist
                      </p>

                      <p className="text-sm text-[#4A6359]">
                        Dr. Neha Agarwal, Gynecologic Oncologist
                      </p>
                    </div>
                  </div>

                  {/* Screening Services */}
                  <div className="flex items-start gap-3">
                    <Search className="w-5 h-5 text-[#8FA998] mt-0.5 flex-shrink-0" />

                    <div>
                      <p className="text-sm font-semibold text-[#1E3F33]">
                        Screening Services
                      </p>

                      <p className="text-sm text-[#4A6359]">
                        Pap Smear, HPV Testing, Pelvic Examination, Cancer Risk Assessment
                      </p>
                    </div>
                  </div>

                  {/* Preventive Care */}
                  <div className="flex items-start gap-3">
                    <Heart className="w-5 h-5 text-[#8FA998] mt-0.5 flex-shrink-0" />

                    <div>
                      <p className="text-sm font-semibold text-[#1E3F33]">
                        Preventive Care
                      </p>

                      <p className="text-sm text-[#4A6359]">
                        Early detection, HPV vaccination, lifestyle counseling, regular follow-up
                      </p>
                    </div>
                  </div>

                  {/* Key Message */}
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-[#8FA998] mt-0.5 flex-shrink-0" />

                    <div>
                      <p className="text-sm font-semibold text-[#1E3F33]">
                        Key Message
                      </p>

                      <p className="text-sm text-[#4A6359]">
                        Early detection and regular screening significantly improve outcomes
                      </p>
                    </div>
                  </div>

                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      {/* ─── CANCER SCREENING & PREVENTIVE ONCOLOGY SECTION ─── */}
      <section className="py-16 md:py-24 bg-[#F2EFE9]" data-testid="screening-about-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">

            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="lg:col-span-7"
            >
              <h2 className="font-serif text-4xl sm:text-5xl tracking-tighter leading-[1] text-[#1E3F33] mb-6">
                What is Cancer Screening & Preventive Oncology?
              </h2>

              <p className="text-base sm:text-lg text-[#4A6359] leading-relaxed mb-6">
                Cancer screening involves tests and examinations to detect cancer
                at an early stage, often before symptoms appear.
              </p>

              <div className="bg-white rounded-2xl p-6 border border-[#D8DDD9]">
                <h3 className="font-serif text-2xl text-[#1E3F33] mb-4">
                  Preventive Oncology Focuses On
                </h3>

                <div className="space-y-4">
                  {[
                    "Identifying risk factors",
                    "Detecting precancerous conditions",
                    "Reducing the chances of developing cancer",
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className="w-2.5 h-2.5 rounded-full bg-[#8FA998] mt-2"></div>

                      <p className="text-[#4A6359] leading-relaxed">
                        {item}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Right Card */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="lg:col-span-5"
            >
              <div className="bg-[#1E3F33] rounded-2xl md:rounded-[2rem] p-7 md:p-8 text-[#F9F9F6] h-full flex flex-col justify-center">
                <h3 className="font-serif text-3xl mb-6">
                  Why Early Detection Matters
                </h3>

                <p className="text-base sm:text-lg leading-relaxed text-[#F9F9F6]/90">
                  Early detection significantly improves treatment success and
                  survival rates.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      {/* ─── SYMPTOMS ─── */}
      <section className="py-20 md:py-28 bg-[#1E3F33]" data-testid="service-symptoms-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            variants={stagger}
          >
            <motion.div variants={fadeUp} className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <AlertTriangle className="w-5 h-5 text-[#C29F60]" />
                <p className="text-sm font-semibold tracking-[0.2em] uppercase text-[#C29F60]">
                  Warning Signs
                </p>
              </div>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#F9F9F6] leading-tight max-w-2xl" data-testid="service-symptoms-heading">
                Symptoms You Should Not Ignore
              </h2>
              <p className="text-base sm:text-lg text-[#8FA998] mt-4 max-w-2xl leading-relaxed">
                Even with regular screening, certain symptoms require immediate attention:
              </p>
            </motion.div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
              {symptoms.map((s, i) => (
                <motion.div
                  key={i}
                  variants={fadeUp}
                  data-testid={`service-symptom-${i}`}
                  className="bg-[#2C5545] rounded-xl p-5 border border-[#8FA998]/15"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-[#C29F60]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-xs font-bold text-[#C29F60]">{i + 1}</span>
                    </div>
                    <p className="text-[#F9F9F6] text-sm sm:text-base font-medium">{s}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            <motion.div
              variants={fadeUp}
              className="relative overflow-hidden rounded-2xl border-2 border-[#C29F60]/60 bg-gradient-to-r from-[#2C5545] to-[#234538] p-6 sm:p-7 shadow-[0_10px_40px_rgba(0,0,0,0.25)]"
            >
              {/* Soft Glow */}
              <div className="absolute inset-0 bg-[#C29F60]/5 pointer-events-none" />

              <div className="relative flex items-start gap-4">

                {/* Alert Icon */}
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#C29F60] text-[#163828] font-bold text-xl shadow-lg">
                  !
                </div>

                {/* Content */}
                <div>
                  <p className="text-[#F4E7CC] text-lg sm:text-xl font-semibold leading-relaxed">
                    If you notice any of these symptoms, consult a{" "}
                    <span className="text-[#F6C978] font-bold">
                      specialist promptly
                    </span>
                    .
                  </p>

                  <p className="mt-2 text-[#D7D7D7] text-sm sm:text-base">
                    Early medical attention can help ensure timely diagnosis and the most effective treatment plan.
                  </p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ─── WHEN TO CONSULT ─── */}
      <section className="py-20 md:py-28 bg-[#F9F9F6]" data-testid="service-consult-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-80px" }}
              variants={fadeUp}
            >
              <p className="text-sm font-semibold tracking-[0.2em] uppercase text-[#8FA998] mb-4">
                Expert Guidance
              </p>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#1E3F33] leading-tight" data-testid="service-consult-heading">
                When to Go for Cancer Screening
              </h2>
              <p className="text-base sm:text-lg text-[#4A6359] mt-4 leading-relaxed">
                You should consider cancer screening if:
              </p>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-80px" }}
              variants={stagger}
              className="space-y-0"
            >
              {whenToConsult.map((item, i) => (
                <motion.div
                  key={i}
                  variants={fadeUp}
                  data-testid={`service-consult-item-${i}`}
                  className="flex items-start gap-4 py-5 border-b border-[#D8DDD9] last:border-0"
                >
                  <div className="w-8 h-8 rounded-lg bg-[#1E3F33]/10 flex items-center justify-center flex-shrink-0">
                    <ChevronRight className="w-4 h-4 text-[#1E3F33]" />
                  </div>
                  <p className="text-base sm:text-lg text-[#1E3F33] font-medium leading-relaxed">{item}</p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* ─── DIAGNOSIS ─── */}
      <section className="py-20 md:py-28 bg-[#F2EFE9]" data-testid="service-diagnosis-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            variants={fadeUp}
            className="mb-12"
          >
            <p className="text-sm font-semibold tracking-[0.2em] uppercase text-[#8FA998] mb-4">
              Diagnosis & Screening
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#1E3F33] leading-tight max-w-2xl" data-testid="service-diagnosis-heading">
              Diagnostic methods include:
            </h2>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-60px" }}
            variants={stagger}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
          >
            {diagnostics.map((d, i) => (
              <motion.div
                key={i}
                variants={fadeUp}
                data-testid={`service-diagnostic-${i}`}
                className="bg-[#F9F9F6] rounded-2xl p-6 hover:-translate-y-1 hover:shadow-xl hover:shadow-[#1E3F33]/5 transition-all duration-300"
              >
                <div className="w-10 h-10 rounded-xl bg-[#1E3F33]/10 flex items-center justify-center mb-4">
                  <Search className="w-5 h-5 text-[#1E3F33]" />
                </div>
                <h3 className="text-base sm:text-lg font-semibold text-[#1E3F33] mb-1">{d.label}</h3>
                <p className="text-sm text-[#4A6359]">{d.detail}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ─── TREATMENT OPTIONS ─── */}
      <section className="py-20 md:py-28 bg-[#F9F9F6]" data-testid="service-treatment-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            variants={fadeUp}
            className="mb-14"
          >
            <p className="text-sm font-semibold tracking-[0.2em] uppercase text-[#8FA998] mb-4">
              Types of Cancer Screening Available
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#1E3F33] leading-tight max-w-3xl" data-testid="service-treatment-heading">
              Dr. Neha Agarwal offers comprehensive screening for gynecologic cancers in Kolkata:
            </h2>
            {/* <p className="text-base sm:text-lg text-[#4A6359] mt-4 max-w-2xl leading-relaxed">
              Dr. Neha Agarwal offers comprehensive screening for gynecologic cancers in Kolkata:          </p> */}
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-60px" }}
            variants={stagger}
            className="grid grid-cols-1 md:grid-cols-2 gap-6"
          >
            {treatments.map((t, i) => (
              <motion.div
                key={i}
                variants={fadeUp}
                data-testid={`service-treatment-card-${i}`}
                className="bg-[#F2EFE9] rounded-2xl md:rounded-[2rem] p-7 md:p-8 hover:-translate-y-1 hover:shadow-xl hover:shadow-[#1E3F33]/5 transition-all duration-300"
              >
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-12 h-12 rounded-xl bg-[#1E3F33]/10 flex items-center justify-center">
                    <t.icon className="w-6 h-6 text-[#1E3F33]" />
                  </div>
                  <h3 className="font-serif text-xl sm:text-2xl text-[#1E3F33] leading-snug">{t.title}</h3>
                </div>
                <p className="text-sm sm:text-base text-[#4A6359] mb-4 leading-relaxed">{t.description}</p>
                <ul className="space-y-2.5">
                  {t.points.map((p, j) => (
                    <li key={j} className="flex items-start gap-2.5">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#8FA998] mt-2 flex-shrink-0" />
                      <span className="text-sm text-[#1E3F33] leading-relaxed">{p}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ─── RECOVERY TIMELINE ─── */}
      {/* <section className="py-20 md:py-28 bg-[#F2EFE9]" data-testid="service-recovery-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24"> */}
      {/* <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            variants={fadeUp}
            className="mb-12"
          >
            <p className="text-sm font-semibold tracking-[0.2em] uppercase text-[#8FA998] mb-4">
              Recovery Journey
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#1E3F33] leading-tight max-w-2xl" data-testid="service-recovery-heading">
              Recovery Timeline
            </h2>
            <p className="text-base sm:text-lg text-[#4A6359] mt-4 max-w-xl leading-relaxed">
              Recovery varies depending on treatment
            </p>
          </motion.div> */}

      {/* <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-60px" }}
            variants={stagger}
            className="grid grid-cols-1 md:grid-cols-3 gap-6"
          >
            {recoveryTimeline.map((phase, i) => (
              <motion.div
                key={i}
                variants={fadeUp}
                data-testid={`service-recovery-phase-${i}`}
                className="bg-[#F9F9F6] rounded-2xl p-7 border border-[#D8DDD9]"
              >
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-8 h-8 rounded-full bg-[#1E3F33] flex items-center justify-center">
                    <span className="text-xs font-bold text-[#F9F9F6]">{i + 1}</span>
                  </div>
                  <h3 className="text-lg font-semibold text-[#1E3F33]">{phase.phase}</h3>
                </div>
                <ul className="space-y-3">
                  {phase.items.map((item, j) => (
                    <li key={j} className="flex items-start gap-2.5">
                      <Clock className="w-4 h-4 text-[#8FA998] mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-[#4A6359] leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </motion.div> */}
      {/* </div> */}
      {/* </section> */}
      {/* ─── PREVENTIVE ONCOLOGY & SCREENING SECTION ─── */}
      <section
        className="py-20 md:py-28 bg-[#1E3F33]"
        data-testid="preventive-oncology-section"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            variants={stagger}
          >
            {/* Heading */}
            <motion.div variants={fadeUp} className="mb-12 text-center">
              <p className="text-sm font-semibold tracking-[0.2em] uppercase text-[#C29F60] mb-4">
                Preventive Cancer Care
              </p>

              <h2 className="font-serif text-4xl sm:text-5xl text-[#F9F9F6] tracking-tight leading-tight mb-5">
                Preventive Oncology & Screening Care
              </h2>

              <p className="text-base sm:text-lg text-[#8FA998] leading-relaxed max-w-3xl mx-auto">
                Early screening and preventive care help reduce cancer risk,
                improve early detection, and support long-term women’s health.
              </p>
            </motion.div>

            {/* GRID */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

              {/* PREVENTIVE SERVICES */}
              <motion.div
                variants={fadeUp}
                className="bg-[#2C5545] rounded-2xl p-7 border border-[#8FA998]/15"
              >
                <h3 className="font-serif text-3xl text-[#F9F9F6] mb-6">
                  Preventive Oncology Services
                </h3>

                <div className="space-y-4">
                  {[
                    "HPV vaccination",
                    "Lifestyle and diet counseling",
                    "Hormonal risk evaluation",
                    "Genetic risk assessment (in selected cases)",
                    "Regular follow-up plans",
                  ].map((item, i) => (
                    <div
                      key={i}
                      className="bg-[#1E3F33] rounded-xl p-4 border border-[#8FA998]/15"
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-6 h-6 rounded-full bg-[#C29F60]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-xs font-bold text-[#C29F60]">
                            {i + 1}
                          </span>
                        </div>

                        <p className="text-[#F9F9F6] text-sm sm:text-base font-medium">
                          {item}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-6 bg-[#1E3F33] border border-[#C29F60]/20 rounded-xl p-5">
                  <p className="text-[#F9F9F6] text-sm sm:text-base leading-relaxed">
                    Prevention is especially important for high-risk individuals.
                  </p>
                </div>
              </motion.div>

              {/* SCREENING VISIT */}
              <motion.div
                variants={fadeUp}
                className="bg-[#2C5545] rounded-2xl p-7 border border-[#8FA998]/15"
              >
                <h3 className="font-serif text-3xl text-[#F9F9F6] mb-6">
                  What Happens During a Screening Visit?
                </h3>

                <div className="space-y-4">
                  {[
                    "Detailed medical history",
                    "Discussion of symptoms and risk factors",
                    "Physical and pelvic examination",
                    "Recommended tests based on age and risk",
                  ].map((item, i) => (
                    <div
                      key={i}
                      className="bg-[#1E3F33] rounded-xl p-4 border border-[#8FA998]/15"
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-6 h-6 rounded-full bg-[#C29F60]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-xs font-bold text-[#C29F60]">
                            {i + 1}
                          </span>
                        </div>

                        <p className="text-[#F9F9F6] text-sm sm:text-base font-medium">
                          {item}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-6 bg-[#1E3F33] border border-[#C29F60]/20 rounded-xl p-5">
                  <p className="text-[#F9F9F6] text-sm sm:text-base leading-relaxed">
                    The process is simple, quick, and usually painless.
                  </p>
                </div>
              </motion.div>

              {/* RECOVERY */}
              <motion.div
                variants={fadeUp}
                className="bg-[#2C5545] rounded-2xl p-7 border border-[#8FA998]/15"
              >
                <h3 className="font-serif text-3xl text-[#F9F9F6] mb-6">
                  Recovery / After Screening
                </h3>

                <div className="space-y-4">
                  {[
                    "Most screening tests require no recovery time",
                    "You can resume normal activities immediately",
                    "Reports are usually available within a few days",
                    "Follow-up is advised if any abnormality is detected",
                  ].map((item, i) => (
                    <div
                      key={i}
                      className="bg-[#1E3F33] rounded-xl p-4 border border-[#8FA998]/15"
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-6 h-6 rounded-full bg-[#C29F60]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-xs font-bold text-[#C29F60]">
                            {i + 1}
                          </span>
                        </div>

                        <p className="text-[#F9F9F6] text-sm sm:text-base font-medium">
                          {item}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>
      {/* ─── FAQ ─── */}
      <section className="py-20 md:py-28 bg-[#F9F9F6]" data-testid="service-faq-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-80px" }}
              variants={fadeUp}
            >
              <p className="text-sm font-semibold tracking-[0.2em] uppercase text-[#8FA998] mb-4">
                Common Questions
              </p>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#1E3F33] leading-tight" data-testid="service-faq-heading">
                Frequently Asked Questions
              </h2>
              <p className="text-base sm:text-lg text-[#4A6359] mt-4 leading-relaxed">
                Find answers to common questions about ovarian cancer diagnosis, treatment options, and recovery.
              </p>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-80px" }}
              variants={fadeUp}
            >
              <Accordion type="single" collapsible className="w-full" data-testid="service-faq-accordion">
                {faqs.map((faq, i) => (
                  <AccordionItem
                    key={i}
                    value={`faq-${i}`}
                    className="border-b border-[#D8DDD9] py-1"
                    data-testid={`service-faq-item-${i}`}
                  >
                    <AccordionTrigger
                      data-testid={`service-faq-trigger-${i}`}
                      className="font-serif text-lg sm:text-xl text-[#1E3F33] hover:no-underline py-5 text-left"
                    >
                      {faq.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-[#4A6359] text-base leading-relaxed pb-5">
                      {faq.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ─── FINAL CTA ─── */}
      <section className="py-20 md:py-28 bg-[#1E3F33]" data-testid="service-final-cta">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            variants={fadeUp}
          >
            <p className="text-sm font-semibold tracking-[0.2em] uppercase text-[#8FA998] mb-6">
              Book Cancer Screening in Kolkata – Early Detection Saves Lives
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#F9F9F6] leading-tight max-w-3xl mx-auto mb-6" data-testid="service-final-cta-heading">
              Regular screening can help detect cancer early or even prevent it altogether. Take a proactive step toward your health with expert guidance
            </h2>
            <p className="text-base sm:text-lg text-[#8FA998] max-w-xl mx-auto leading-relaxed mb-10">
              Consult Dr. Neha Agarwal for cancer screening and preventive oncology services in Kolkata.
            </p>

            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="/book-appointment"
                data-testid="service-cta-book-btn"
                className="inline-flex items-center gap-2 bg-[#F9F9F6] text-[#1E3F33] px-8 py-4 rounded-full text-sm font-bold hover:bg-[#E6E7E2] transition-colors"
              >
                <Calendar className="w-4 h-4" />
                Book Appointment
              </a>
              <a
                href="tel:+916291000829"
                data-testid="service-cta-call-btn"
                className="inline-flex items-center gap-2 border-2 border-[#F9F9F6] text-[#F9F9F6] px-8 py-4 rounded-full text-sm font-bold hover:bg-[#F9F9F6] hover:text-[#1E3F33] transition-all"
              >
                <Phone className="w-4 h-4" />
                Get Second Opinion
              </a>
              <a
                href="https://wa.me/916291000829"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="service-cta-whatsapp-btn"
                className="inline-flex items-center gap-2 bg-[#2C5545] text-[#F9F9F6] px-8 py-4 rounded-full text-sm font-bold hover:bg-[#8FA998]/30 transition-colors"
              >
                <MessageCircle className="w-4 h-4" />
                WhatsApp
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ─── SEO FOOTER ─── */}
      <footer className="bg-[#1E3F33] border-t border-[#F9F9F6]/10 py-12" data-testid="service-footer">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <h3 className="font-serif text-lg text-[#F9F9F6]/50 mb-3">
            Cancer Screening & Preventive Oncology in Kolkata
          </h3>
          <p className="text-xs text-[#F9F9F6]/30 leading-relaxed max-w-3xl mb-6">
            Looking for cancer screening in Kolkata? Dr. Neha Agarwal offers Pap smear, HPV testing, ovarian cancer screening, and preventive oncology services for women across Mukundapur, EM Bypass, Garia, Kasba, Ballygunge, Salt Lake, and nearby areas.
          </p>
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4 pt-6 border-t border-[#F9F9F6]/10">
            <p className="text-xs text-[#F9F9F6]/30">
              &copy; {new Date().getFullYear()} Dr. Neha Agarwal. All rights reserved.
            </p>
            <Link to="/" className="text-xs text-[#F9F9F6]/30 hover:text-[#F9F9F6]/60 transition-colors flex items-center gap-1">
              <ArrowLeft className="w-3 h-3" /> Back to Home
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
